import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { validations } from "./validations";
import { Button, Card, CardActions, CardContent, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEdicionSocio } from "../../../../context/socio/use-edicion-socio";
import EndPoints from "../../../../utils/services/EndPoints";
import Api from "../../../../utils/services/Api";
import { ContainedBackdrop } from "../../../../components/Backdrop/Backdrop";
import DomicilioSection from "./DomicilioSection";
import TrabajoSection from "./TrabajoSection";
import { getCodigoUsuario } from "../../../../utils/Common";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";

const useStyles = makeStyles(() => ({
  cardActions: {
    display: "flex",
    justifyContent: "space-between",
  },
  paperContainer: {
    padding: 16
  }
}));

const tiposDireccion = [
  {
    value: 1,
    label: "Urbano"
  },
  {
    value: 2,
    label: "Rural"
  }
];

const DireccionForm = () => {
  const {
    infoSocio: {
      datosBasicosMs: {
        codigoPersona
      }
    },
    goNextStep,
    goPreviousStep,
  } = useEdicionSocio()
  const { enqueueSnackbar } = useSnackbar();
  const currentDireccion = useRef({})
  const [loading, setLoading] = useState(true)
  const classes = useStyles();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    values,
    touched,
    errors,
    isSubmitting,
  } = useFormik({
    initialValues: {
      idTipoDireccion: "",
      idTipoVivienda: "",
      barrioComunidad: "",
      direccion: "",
      referencia: "",
      sector: "",
      paisDomicilio: "",
      provinciaDomicilio: "",
      ciudadDomicilio: "",
      parroquiaDomicilio: '',
      idTipoDireccionTrabajo: "",
      barrioComunidadTrabajo: "",
      direccionTrabajo: "",
      referenciaTrabajo: "",
      sectorTrabajo: "",
      idPais: "",
      idProvincia: "",
      idCiudad: "",
      idParroquia: '',
      telefonoConvencional: '',
      telefonoCelular: '',
      telefonoConvencionalT: '',
      telefonoCelularT: '',
      lugarTrabajo: ''
    },
    validationSchema: yup.object().shape(validations),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { personaDireccionMs, personaTrabajoMs } = currentDireccion.current;
        const usuario = getCodigoUsuario();
        const datePersonaAddress = {
          personaDireccionMs: {
            codigo: personaDireccionMs.codigo ?? 0,
            barrioComunidad: values.barrioComunidad,
            idParroquia: { codigo: values.idParroquia },
            idCiudad: { codigo: values.idCiudad },
            idProvincia: { codigo: values.idProvincia },
            idPais: { codigo: values.idPais },
            codigoTipoDireccion: values.idTipoDireccion,
            codigoUsuarioActualiza: usuario,
            codigoUsuarioRegistra: usuario,
            direccion: values.direccion,
            fechaActualiza: new Date(),
            fechaRegistro: new Date(),
            fechaResidencia: new Date(),
            referencia: values.referencia,
            sector: values.sector,
            personaMs: {
              codigo: codigoPersona
            },
            tipoVivienda: {
              codigo: values.idTipoVivienda,
            },
            telefonoConvencionalString: values.telefonoConvencional,
            telefonoCelularString: values.telefonoCelular,
          },
          personaTrabajoMs: {
            codigo: personaTrabajoMs.codigo ?? 0,
            barrioComunidad: values.barrioComunidadTrabajo,
            codigoDivisionPolitica: personaTrabajoMs.codigoDivisionPolitica,
            codigoTipoDireccion: values.idTipoDireccionTrabajo,
            codigoUsuarioActualiza: usuario,
            codigoUsuarioRegistra: usuario,
            direccion: values.direccionTrabajo,
            fechaActualiza: new Date(),
            fechaRegistro: new Date(),
            referencia: values.referenciaTrabajo,
            sector: values.sectorTrabajo,
            personaMs: {
              codigo: codigoPersona
            },
            telefonoConvencionalString: values.telefonoConvencionalT,
            telefonoCelularString: values.telefonoCelularT,
            lugarTrabajoString: values.lugarTrabajo
          },
        };
        await Api(EndPoints.savePersonAddress, "POST", datePersonaAddress)
        enqueueSnackbar("Datos actualizados correctamente", { variant: "success" })
      } catch (error) {
        enqueueSnackbar(error.message, { variant: "error" })
      } finally {
        setSubmitting(false)
      }
      goNextStep()
    },
  });

  const getDireccionSocio = async () => {
    try {
      const response = await Api(
        EndPoints.getDirecciones + "/" + codigoPersona,
        "GET",
        null
      )
      currentDireccion.current = response
      if (response.personaDireccionMs.codigo) {
        setValues({
          idTipoDireccion: response.personaDireccionMs.codigoTipoDireccion,
          idTipoVivienda: response.personaDireccionMs.tipoVivienda.codigo,
          idParroquia: response.personaDireccionMs.idParroquia.codigo,
          idCiudad: response.personaDireccionMs.idCiudad.codigo,
          idProvincia: response.personaDireccionMs.idProvincia.codigo,
          idPais: response.personaDireccionMs.idPais.codigo,
          barrioComunidad: response.personaDireccionMs.barrioComunidad,
          direccion: response.personaDireccionMs.direccion,
          referencia: response.personaDireccionMs.referencia,
          sector: response.personaDireccionMs.sector,
          idTipoDireccionTrabajo: response.personaTrabajoMs.codigoTipoDireccion,
          barrioComunidadTrabajo: response.personaTrabajoMs.barrioComunidad,
          direccionTrabajo: response.personaTrabajoMs.direccion,
          referenciaTrabajo: response.personaTrabajoMs.referencia,
          sectorTrabajo: response.personaTrabajoMs.sector,
          telefonoConvencional: response.personaDireccionMs.telefonoConvencionalString,
          telefonoCelular: response.personaDireccionMs.telefonoCelularString,
          telefonoConvencionalT: response.personaTrabajoMs.telefonoConvencionalString,
          telefonoCelularT: response.personaTrabajoMs.telefonoCelularString,
          lugarTrabajo: response.personaTrabajoMs.lugarTrabajoString,
        })
      }
      setLoading(false)
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" })
    }
  };

  useEffect(() => {
    getDireccionSocio();
  }, []);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} position="relative">
        <Card
          component="form"
          onSubmit={handleSubmit}
          style={{
            position: "absolute"
          }}
        >
          <ContainedBackdrop open={loading} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} component={Typography} variant="h5">
                Domicilio
              </Grid>
              <Grid item xs={12}>
                <DomicilioSection
                  tiposDireccion={tiposDireccion}
                  paperContainerClass={classes.paperContainer}
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  isSubmitting={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} component={Typography} variant="h5">
                Trabajo
              </Grid>
              <Grid item xs={12}>
                <TrabajoSection
                  tiposDireccion={tiposDireccion}
                  paperContainerClass={classes.paperContainer}
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  isSubmitting={isSubmitting}
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions
            classes={{
              root: classes.cardActions
            }}
          >
            <Button
              color="primary"
              onClick={goPreviousStep}
              disabled={isSubmitting}
            >
              Atrás
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={isSubmitting}
            >
              <span>Siguiente</span>
            </LoadingButton>
          </CardActions>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DireccionForm;
